<script>
import axios from 'axios';
import moment from 'moment';
import { cmsUrl } from '../variables';
import LoadIndicator from '../components/LoadIndicator';

export default {
    name: 'EventForm',
    components: {
        LoadIndicator,
    },
    data() {
        return {
            offeringId: null,
            eventData: { page_title: '', description: '', link: '', dates: [] },
            isLoading: false,
            isSaving: false,
            sendError: false,
            isValidated: false,
            errors: [],
        }
    },
    mounted() {
        this.offeringId = this.$route.params.offeringId ? this.$route.params.offeringId : null;
        if (this.offeringId)
            this.init(this.offeringId)
    },
    methods: {
        init(id) {
            this.isLoading = true;
            axios
                .get(cmsUrl + '/organisations/post?id=' + id)
                .then(this.onDataLoaded)
                .catch(() => this.onError())
        },
        onDataLoaded(data) {
            if (!data || !data.data) {
                this.onError();
                return;
            }
            this.eventData = data.data;
            this.isLoading = false;

            this.eventData.dates = data.data.dates ? data.data.dates : [];
            this.eventData.dates.map(dateEntry => {
                dateEntry.date = moment(new Date(dateEntry.date)).format('DD.MM.YYYY'); 
            });
        },
        onError() {
            this.isLoading = false;
        },
        onSubmit(e) {
            e.preventDefault();
            if (this.isSaving) return;

            this.isValidated = false;
            
            this.errors = [];
            if (!this.eventData.post_title)
                this.errors.push({concern: 'title', msg: this.$t('eventForm.filedValidationMsgTitle')});
            if (!/^(http|https):\/\/[^ "]+$/.test(this.eventData.link))
                this.errors.push({concern: 'link', msg: this.$t('eventForm.filedValidationMsgLink')});
            if (this.eventData.dates.length === 0)
                this.errors.push({concern: 'dates', msg: this.$t('eventForm.filedValidationMsgDates')});
            if (this.eventData.dates.find(item => { return !this.checkDate(item.date); }))
                this.errors.push({concern: 'date', msg: ''});
            if (this.errors.length > 0) {
                this.isValidated = true;
                this.scrollToFormTop();
                return;
            }
            
            this.isSaving = true;
            this.sendError = false;

            let sendData = { ...this.eventData };

            let dates = [];
            for (let i = 0; i < sendData.dates.length; i++) {
                let element = sendData.dates[i];
                const splitted = element.date.split('.');
                element.date = splitted[1] + '/' + splitted[0] + '/' + splitted[2];
                dates.push(element);
            }

            axios
                .post(cmsUrl + '/organisations/post', {
                    id: this.offeringId,
                    organisation_id: this.$route.params.organisationId,
                    title: sendData.post_title,
                    description: sendData.description,
                    dates: dates,
                    link: sendData.link,
                    type: 'event',
                },
                {
                    headers: { }
                })
                .then(() => {
                    this.isSaving = false;
                    this.$router.push('/organisation/' + this.$route.params.organisationId);
                })
                .catch(() => {
                    this.isSaving = false;
                    this.sendError = true;
                    this.scrollToFormTop();
                })
            
        },
        checkDate(dateString) {
            const splitted = dateString.split('.');
            if (splitted.length !== 3)
                return false;
            if (splitted[0].length !== 2 
                && splitted[1].length !== 2 
                && splitted[2].length !== 4)
                    return false;
            return true;
        },
        onCancel(e) {
            e.preventDefault();
            this.$router.push('/organisation/' + this.$route.params.organisationId);
        },
        scrollToFormTop() {
            setTimeout(() => {
                window.scrollTo(0, this.$refs.form.offsetTop - 100);
            }, 100)
        },
        removeDateEntry(i, e) {
            e.preventDefault();
            this.eventData.dates.splice(i, 1);
            this.isValidated = false;
        },
        addDateEntry(e) {
            e.preventDefault();
            this.eventData.dates.push({ date: '', time: '', location: '' });
            this.isValidated = false;
        }
    },
    computed: {
        routerKey() {
            return this.$route.path;
        },
    },
};
</script>

<template>
    <section class="event-form">
        <h1 class="event-form__title page-title">
            <span v-if="$route.params.offeringId">{{$t('eventForm.titleEdit')}}</span>
            <span v-if="!$route.params.offeringId">{{$t('eventForm.titleCreate')}}</span>
        </h1>

        <form ref="form" action="" class="event-form__form" v-if="!isLoading">
            <div class="event-form__error">
                <h2 class="event-form__error-title" v-if="errors.length > 0">{{$t('eventForm.formValidationError')}}</h2>
                <h2 class="event-form__error-title" v-if="sendError">{{$t('eventForm.sendError')}}</h2>
            </div>

            <div class="event-form__input-group form-control">
                <label class="event-form__label form-control__label" for="">{{$t('eventForm.inputLabelTitle')}}</label>
                <input class="event-form__control form-control__input" type="text" name="title" v-model="eventData.post_title">
                <div class="event-form__error-msg" v-if="errors.find(item => item.concern === 'title')">{{errors.find(item => item.concern === 'title').msg}}</div>
            </div>

            <div class="event-form__input-group form-control">
                <label class="event-form__label form-control__label" for="">{{$t('eventForm.inputLabelDescription')}}</label>
                <textarea class="event-form__control form-control__input" name="description" rows="3" v-model="eventData.description"></textarea>
            </div>

            <div class="event-form__input-group form-control">
                <label class="event-form__label form-control__label" for="">{{$t('eventForm.inputLabelLink')}}</label>
                <input class="event-form__control form-control__input" type="text" name="link" v-model="eventData.link" placeholder="https://domain.ch">
                <div class="event-form__error-msg" v-if="errors.find(item => item.concern === 'link')">{{errors.find(item => item.concern === 'link').msg}}</div>
            </div>

            <div class="event-form__input-group form-control">
                <h3 class="event-form__input-group__title form-control__label">{{$t('eventForm.inputLabelDates')}}</h3>
                <div class="event-form__date-entry" v-for="(date, i) in eventData.dates" v-bind:key="i">
                    <div class="event-form__date-entry__controls">
                        <button class="event-form__remove-btn" v-on:click="removeDateEntry(i, $event)"></button>
                        <input class="event-form__control form-control__input" type="text" name="date" v-model="date.date" placeholder="DD/MM/JJJJ">
                        <input class="event-form__control form-control__input" type="text" name="time" v-model="date.time" placeholder="MM:HH">
                        <input class="event-form__control form-control__input" type="text" name="location" v-model="date.location" placeholder="Veranstaltungsort">
                    </div>
                    <div class="event-form__error-msg" v-if="isValidated && !date.date">{{$t('eventForm.filedValidationMsgDate')}}</div>
                </div>
                <div class="event-form__error-msg" v-if="errors.find(item => item.concern === 'dates')">{{errors.find(item => item.concern === 'dates').msg}}</div>
                <button class="event-form__add-line-btn" v-on:click="addDateEntry($event)">{{$t('eventForm.addDateEntry')}}</button>
            </div>

            <p class="event-form__draft-note" v-if="!$route.params.offeringId">{{$t('eventForm.draftNote')}}</p>

            <div class="event-form__actions">
                <button class="button button--primary" v-on:click="onSubmit($event)"><span v-if="!isSaving">{{$t('eventForm.buttonSave')}}</span><span v-if="isSaving">{{$t('eventForm.loading')}}</span></button>
                <button class="button button--secondary" v-on:click="onCancel($event)">{{$t('eventForm.buttonCancel')}}</button>
            </div>
        </form>
        <load-indicator v-if="isLoading" />
    </section>
</template>

<style lang="scss" scoped>
.event-form__actions {
    margin-top: 8rem;
}
.event-form__draft-note {
    max-width: 41.6rem;
    margin:  6.4rem 0 -3.2rem;
}
.event-form__error-title {
    margin-bottom: 4rem;
    font-weight: 300;
    font-size: 2rem;
    color: $color-danger;
}
.event-form__error-msg {
    margin: -1.6rem 0 2.4rem;
    font-size: 1.44rem;
    color: $color-danger;
}

.event-form__input-group__title {
    margin: 4.8rem 0 1.6rem;
    font-weight: 300;
}
.event-form__date-entry__controls {
    position: relative;
    display: flex;
    margin-bottom: 0;

    @include breakpoint('xs-down') {
        display: block;
        margin-bottom: 4.8rem;
    }
}
.event-form__date-entry__controls .event-form__control {
    margin-bottom: 1.6rem;
}
.event-form__date-entry__controls .event-form__control:last-child() {
    padding-right: 3.2rem;
}
.event-form__remove-btn {
    position: absolute;
    right: 0; top: 0.8rem;
    width: 2.4rem; height: 2.4rem;
    outline: none;
    border: none;
    margin: 0;
    padding: 0;
    background: center transparent no-repeat url('~@/assets/images/ico-delete.svg');
    cursor: pointer;
}
.event-form__add-line-btn {
    outline: none;
    border: none;
    margin: 0;
    padding: 0;
    font-size: 1.44rem;
    letter-spacing: 0.08rem;
    text-transform: uppercase;
    color: $color-secondary;
    cursor: pointer;
}
</style>
